import type { RecordingHistoryItem } from '../../lib/api';
import { dayAfter, midnightIn } from '../../lib/time';

/**
 * How a past walkthrough reads in the list (TOADO-439).
 *
 * Pure, so the wording is tested without a page. A walkthrough has no name of its
 * own, and a column of dates is a list nobody can find anything in, so a row leads
 * with the first ticket it filed.
 */

/** Its first ticket, and how many more it filed. */
export function walkthroughHeadline(
  item: Pick<RecordingHistoryItem, 'firstTicketTitle' | 'ticketCount'>,
): string {
  if (!item.firstTicketTitle) return item.ticketCount === 0 ? 'Walkthrough with no tickets' : 'Walkthrough';
  const more = item.ticketCount - 1;
  return more > 0 ? `${item.firstTicketTitle} and ${more} more` : item.firstTicketTitle;
}

/** How long it ran, in the unit somebody would say it in. Null when it never ended. */
export function walkthroughLength(startedAt: string, endedAt?: string): string | null {
  if (!endedAt) return null;
  const ms = Date.parse(endedAt) - Date.parse(startedAt);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const seconds = Math.round(ms / 1_000);
  return seconds < 60 ? `${seconds} sec` : `${Math.round(seconds / 60)} min`;
}

export type WalkthroughVideo =
  | { kind: 'kept'; until: string }
  | { kind: 'expired' }
  | { kind: 'none' };

/**
 * What is left of its video: kept until a date, aged out, or never made.
 *
 * "Kept" wins while any clip remains, and the date is the FIRST remaining clip's,
 * which is when the walkthrough starts losing video rather than when it has lost it
 * all. A walkthrough whose clips failed or never existed has nothing to expire.
 */
export function walkthroughVideo(
  item: Pick<RecordingHistoryItem, 'clipsKept' | 'clipsExpired' | 'videoKeptUntil'>,
): WalkthroughVideo {
  if (item.clipsKept > 0 && item.videoKeptUntil) return { kind: 'kept', until: item.videoKeptUntil };
  if (item.clipsExpired > 0) return { kind: 'expired' };
  return { kind: 'none' };
}

/** The sentence for it, naming audio for a narration-only walkthrough. */
export function walkthroughVideoLabel(
  video: WalkthroughVideo,
  mediaMode: RecordingHistoryItem['mediaMode'],
  formatDate: (iso: string) => string,
): string {
  const noun = mediaMode === 'audio' ? 'Audio' : 'Video';
  if (video.kind === 'kept') return `${noun} kept until ${formatDate(video.until)}`;
  if (video.kind === 'expired') return `${noun} expired`;
  return `No ${noun.toLowerCase()} kept`;
}

/**
 * Which page numbers to offer (TOADO-439): the first and the last, the current page
 * and its neighbours, with a gap where pages are skipped. Seven or fewer are all
 * shown. A gap that would hide a single page shows the page instead, since a gap is
 * no shorter than the number it stands for.
 */
export function pageWindow(current: number, totalPages: number): Array<number | 'gap'> {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  const wanted = new Set([1, totalPages, current - 1, current, current + 1]);
  // Near either end, keep the row the same width rather than shrinking it.
  if (current <= 3) [2, 3, 4].forEach((page) => wanted.add(page));
  if (current >= totalPages - 2) [totalPages - 3, totalPages - 2, totalPages - 1].forEach((page) => wanted.add(page));
  const pages = [...wanted].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
  const shown: Array<number | 'gap'> = [];
  let previous = 0;
  for (const page of pages) {
    if (page - previous === 2) shown.push(previous + 1);
    else if (page - previous > 2) shown.push('gap');
    shown.push(page);
    previous = page;
  }
  return shown;
}

/** "Showing 21 to 40 of 1,284", for the page being looked at. */
export function showingRange(page: number, pageSize: number, total: number, itemsOnPage: number): string {
  if (total === 0 || itemsOnPage === 0) return '';
  const from = (page - 1) * pageSize + 1;
  const to = from + itemsOnPage - 1;
  return `Showing ${from.toLocaleString()} to ${to.toLocaleString()} of ${total.toLocaleString()}`;
}

/** The page sizes the list offers (TOADO-441). The server accepts these and no others. */
export const HISTORY_PAGE_SIZES = [20, 50, 100] as const;


/**
 * The instants a From and a To date stand for (TOADO-441), in the person's own time
 * zone: from the start of From to the start of the day after To, so To includes the
 * whole of that day and From alone, or To alone, is an open-ended range. Picked the
 * wrong way round, it is the same range.
 */
export function dayRange(fromDate: string, toDate: string): { from?: string; to?: string } {
  // Days in the person's zone, the one the list shows its times in (TOADO-904).
  let [first, last] = [fromDate, toDate];
  if (first && last && first > last) [first, last] = [last, first];
  const start = first ? midnightIn(first) : null;
  const after = last ? midnightIn(dayAfter(last)) : null;
  return {
    ...(start ? { from: start.toISOString() } : {}),
    ...(after ? { to: after.toISOString() } : {}),
  };
}
